import { User, LoginCredentials, AuthResponse, LoginOutcome } from '@/types'
import { API_BASE_URL, getAuthHeaders, withRetry, handleResponse, apiRequestWithAutoRefresh, ApiError } from '../utils/apiUtils'

/** 登入第一步的結果：成功時帶回 token；需要兩步驟驗證時帶回第二步的憑證 */
export type LoginResult =
  | { status: 'success'; auth: AuthResponse }
  | Exclude<LoginOutcome, { status: 'success' }>

const MFA_SETUP_FALLBACK_MESSAGE = '這個帳號必須開啟兩步驟驗證，請先到會員中樞完成設定再登入'
const NETWORK_ERROR_MESSAGE = '無法連線，請檢查網路後再試'

/**
 * 解讀 /auth/login、/auth/google 的回應。
 * 403 MFA_REQUIRED／MFA_SETUP_REQUIRED 不是登入失敗，是要多做一步：轉成結果回傳，不丟錯，
 * 也就不會有帶著 mfaToken 的錯誤物件被印進 console。
 */
const parseLoginResponse = async (response: Response): Promise<LoginResult> => {
  if (response.status === 403) {
    const body = await response.clone().json().catch(() => null)
    if (body?.code === 'MFA_REQUIRED' && typeof body.data?.mfaToken === 'string' && body.data.mfaToken) {
      const expiresIn = Number(body.data.expiresIn)
      return {
        status: 'mfa_required',
        mfaToken: body.data.mfaToken,
        expiresIn: Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 300
      }
    }
    if (body?.code === 'MFA_SETUP_REQUIRED') {
      return { status: 'mfa_setup_required', message: body.errors?.[0] || body.message || MFA_SETUP_FALLBACK_MESSAGE }
    }
  }
  const result = await handleResponse(response)
  return { status: 'success', auth: result.data }
}

export const authService = {
  // Login user
  async login(credentials: LoginCredentials): Promise<LoginResult> {
    return withRetry(async () => {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      })

      return parseLoginResponse(response)
    })
  },

  // Google login
  async googleLogin(idToken: string): Promise<LoginResult> {
    return withRetry(async () => {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken })
      })

      return parseLoginResponse(response)
    })
  },

  /**
   * 兩步驟驗證第二步。失敗丟 ApiError，code 是後端（照中樞）的錯誤代碼：
   * MFA_INVALID_CODE、MFA_CODE_REUSED（留在畫面重打）、MFA_TOKEN_INVALID（回登入第一步）、
   * MFA_LOCKED（retryAfterMs 是要等多久）、MFA_SETUP_REQUIRED、HUB_UNAVAILABLE。
   * 不經過 withRetry：429 是鎖定，自動重送只會白白浪費嘗試次數。
   */
  async verifyMfa(mfaToken: string, code: string): Promise<AuthResponse> {
    let response: Response
    try {
      response = await fetch(`${API_BASE_URL}/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mfaToken, code })
      })
    } catch {
      throw new ApiError(NETWORK_ERROR_MESSAGE, 0, [NETWORK_ERROR_MESSAGE], undefined, 'NETWORK_ERROR')
    }

    const body = await response.json().catch(() => ({}))
    if (!response.ok) {
      const message = body.errors?.[0] || body.message || '驗證失敗，請稍後再試'
      const error = new ApiError(message, response.status, body.errors ?? [], undefined,
        typeof body.code === 'string' ? body.code : undefined)
      if (response.status === 429) {
        const seconds = Number(response.headers.get('Retry-After') ?? body.retryAfterSeconds)
        if (Number.isFinite(seconds) && seconds > 0) error.retryAfterMs = seconds * 1000
      }
      throw error
    }
    return body.data
  },

  // Get current user
  async getCurrentUser(): Promise<User> {
    return withRetry(async () => {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      const result = await handleResponse(response)
      return result.data
    })
  },

  // Update user profile
  async updateProfile(userData: Partial<User>): Promise<User> {
    const result = await apiRequestWithAutoRefresh('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
    return result.data
  },

  // Update bank information
  async updateBankInfo(bankInfo: any): Promise<User> {
    const result = await apiRequestWithAutoRefresh('/auth/bank-info', {
      method: 'PUT',
      body: JSON.stringify(bankInfo)
    })
    return result.data
  },

  // Change password
  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    await apiRequestWithAutoRefresh('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  /**
   * 登出時通知後端讓這筆登入失效（後端會清掉存著的中樞 refresh token）。
   * 前端清 token 不用等它；失敗也不影響登出。
   */
  async revokeSession(refreshToken: string): Promise<void> {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
      keepalive: true
    })
  },
}

export default authService
